// @migration-in-progress
/**
 * Defect Export Module - PDF generation for defect logs and management reports
 * 
 * Provides comprehensive defect tracking documentation including status summaries,
 * individual defect reports, and compliance tracking for vessel maintenance
 */

import { debug } from '../logging';
import {
  createPDF,
  getPageDimensions,
  setFont,
  checkPageBreak,
  addReportHeader,
  addReportFooter,
  addSectionHeading,
  addWrappedText,
  formatDate,
  formatDateTime,
  savePDF,
  handlePDFError,
  PDF_CONFIG
} from './pdfCore';

/**
 * Export defect records as comprehensive PDF report
 * @param {Array} defects - Array of defect records
 * @param {Object} options - Export options
 * @returns {boolean} Success status
 */
export function exportDefectsReportPDF(defects, options = {}) {
  try {
    const {
      vesselName = 'Unknown Vessel',
      category = 'Unknown Category',
      reportTitle = 'Defect Log Report',
      includeResolved = true
    } = options;

    if (!defects || defects.length === 0) {
      alert('No defects found to export.');
      return false;
    }

    // Filter defects if needed
    const filteredDefects = includeResolved ? defects : defects.filter(d => d.status !== 'Closed');

    const pdf = createPDF();
    const { margin, contentWidth, pageWidth } = getPageDimensions(pdf);
    
    let currentY = margin;

    // Add header
    currentY = addReportHeader(pdf, currentY, {
      title: reportTitle,
      vesselName,
      category,
      filters: !includeResolved ? { status: 'Open and In Progress only' } : {}
    });

    // Add Official Number (MCA requirement)
    if (options.vessel && options.vessel.officialNumber) {
      setFont(pdf, 'body');
      pdf.text(`Official Number: ${options.vessel.officialNumber}`, margin, currentY);
      currentY += PDF_CONFIG.lineHeight;
    }

    // Summary information
    setFont(pdf, 'body');
    pdf.text(`Total Defects: ${filteredDefects.length}`, margin, currentY);
    currentY += PDF_CONFIG.sectionSpacing;

    // Generate summary statistics
    const openDefects = filteredDefects.filter(d => d.status === 'Open').length;
    const inProgressDefects = filteredDefects.filter(d => d.status === 'In Progress').length;
    const closedDefects = filteredDefects.filter(d => d.status === 'Closed').length;
    const criticalDefects = filteredDefects.filter(d => d.risk_level === 'Critical' && d.status !== 'Closed').length;

    // Summary section
    currentY = addSectionHeading(pdf, currentY, 'Summary:', 'subheading');
    setFont(pdf, 'body');
    pdf.text(`  • Open: ${openDefects}`, margin + 5, currentY);
    currentY += PDF_CONFIG.lineHeight;
    pdf.text(`  • In Progress: ${inProgressDefects}`, margin + 5, currentY);
    currentY += PDF_CONFIG.lineHeight;
    pdf.text(`  • Closed: ${closedDefects}`, margin + 5, currentY);
    currentY += PDF_CONFIG.lineHeight;
    pdf.text(`  • Critical Open: ${criticalDefects}`, margin + 5, currentY);
    currentY += PDF_CONFIG.sectionSpacing;

    // Separator
    pdf.setLineWidth(0.5);
    pdf.line(margin, currentY, pageWidth - margin, currentY);
    currentY += PDF_CONFIG.sectionSpacing;

    // Defect Records Section
    currentY = addSectionHeading(pdf, currentY, 'Defect Records', 'heading');

    filteredDefects.forEach((defect, index) => {
      const pageBreak = checkPageBreak(pdf, currentY, () => addReportHeader(pdf, margin, {
        title: `${reportTitle} (continued)`,
        vesselName,
        category
      }), 35);
      currentY = pageBreak.currentY;

      // Defect header
      setFont(pdf, 'subheading');
      pdf.text(`${index + 1}. ${defect.reference_id || `DEF-${defect.id}`}`, margin, currentY);
      currentY += PDF_CONFIG.lineHeight;

      // Status and risk level
      setFont(pdf, 'small');
      const statusColor = defect.status === 'Closed' ? [34, 197, 94] : 
                         defect.status === 'In Progress' ? [234, 179, 8] : 
                         [239, 68, 68];
      
      // Status badge
      pdf.setFillColor(...statusColor);
      pdf.roundedRect(margin, currentY, 25, 6, 1, 1, 'F');
      pdf.setTextColor(255, 255, 255);
      pdf.text(defect.status.toUpperCase(), margin + 12.5, currentY + 4, { align: 'center' });
      pdf.setTextColor(0, 0, 0);
      
      // Risk level badge if critical
      if (defect.risk_level === 'Critical') {
        pdf.setFillColor(239, 68, 68);
        pdf.roundedRect(margin + 30, currentY, 20, 6, 1, 1, 'F');
        pdf.setTextColor(255, 255, 255);
        pdf.text('CRITICAL', margin + 40, currentY + 4, { align: 'center' });
        pdf.setTextColor(0, 0, 0);
      }
      
      currentY += 10;

      // Defect details
      setFont(pdf, 'body');
      pdf.text(`Reported: ${formatDate(defect.date_reported)}`, margin + 5, currentY);
      currentY += PDF_CONFIG.lineHeight;
      
      if (defect.location) {
        pdf.text(`Location: ${defect.location}`, margin + 5, currentY);
        currentY += PDF_CONFIG.lineHeight;
      }
      
      if (defect.category) {
        pdf.text(`Category: ${defect.category}`, margin + 5, currentY);
        currentY += PDF_CONFIG.lineHeight;
      }

      // Description
      if (defect.description) {
        pdf.text('Description:', margin + 5, currentY);
        currentY += PDF_CONFIG.lineHeight;
        currentY = addWrappedText(pdf, currentY, defect.description, { 
          fontType: 'body', 
          indent: 10,
          maxWidth: contentWidth - 15
        });
      }

      // Resolution information
      if (defect.status === 'Closed' && defect.resolution) {
        pdf.text('Resolution:', margin + 5, currentY);
        currentY += PDF_CONFIG.lineHeight;
        currentY = addWrappedText(pdf, currentY, defect.resolution, { 
          fontType: 'body', 
          indent: 10,
          maxWidth: contentWidth - 15
        });
        
        if (defect.date_resolved) {
          pdf.text(`Resolved: ${formatDate(defect.date_resolved)}`, margin + 5, currentY);
          currentY += PDF_CONFIG.lineHeight;
        }
      }

      // Action required
      if (defect.action_required && defect.status !== 'Closed') {
        pdf.text('Action Required:', margin + 5, currentY);
        currentY += PDF_CONFIG.lineHeight;
        currentY = addWrappedText(pdf, currentY, defect.action_required, { 
          fontType: 'body', 
          indent: 10,
          maxWidth: contentWidth - 15
        });
      }

      currentY += PDF_CONFIG.sectionSpacing / 2; // Space between defects
    });

    // Add footer
    addReportFooter(pdf, {
      customText: 'Vessel Maintenance and Defect Tracking System'
    });

    // Save the PDF
    const statusStr = !includeResolved ? '_open_only' : '';
    const filename = savePDF(pdf, 'defect_log_report', new Date(), statusStr);
    
    debug.log('Defects PDF export completed', { 
      filename, 
      defectCount: filteredDefects.length,
      includeResolved
    });
    
    return true;
    
  } catch (error) {
    handlePDFError(error, 'defects report export');
    return false;
  }
}

/**
 * Export individual defect as detailed PDF report
 * @param {Object} defect - Defect record
 * @param {Object} options - Export options
 * @returns {boolean} Success status
 */
export function exportIndividualDefectPDF(defect, options = {}) {
  try {
    const {
      vesselName = 'Unknown Vessel',
      category = 'Unknown Category'
    } = options;

    if (!defect) {
      alert('No defect data provided for export.');
      return false;
    }

    const pdf = createPDF();
    const { margin, contentWidth, pageWidth } = getPageDimensions(pdf);
    
    let currentY = margin;

    // Header with vessel information
    setFont(pdf, 'title');
    pdf.text('DEFECT REPORT', margin, currentY);
    currentY += 10;

    // Vessel details box
    setFont(pdf, 'body');
    pdf.setFillColor(248, 250, 252);
    pdf.rect(margin, currentY, contentWidth, 20, 'F');
    pdf.setDrawColor(226, 232, 240);
    pdf.rect(margin, currentY, contentWidth, 20, 'S');
    
    currentY += 5;
    pdf.text(`Vessel: ${vesselName}`, margin + 5, currentY);
    currentY += PDF_CONFIG.lineHeight;
    pdf.text(`Category: ${category}`, margin + 5, currentY);
    currentY += PDF_CONFIG.lineHeight + 8;

    // Defect reference and status
    setFont(pdf, 'subheading');
    pdf.text(`Defect ID: ${defect.reference_id || `DEF-${defect.id}`}`, margin, currentY);
    currentY += PDF_CONFIG.lineHeight + 3;

    // Status with color coding
    const statusColor = defect.status === 'Closed' ? [34, 197, 94] : 
                       defect.status === 'In Progress' ? [234, 179, 8] : 
                       [239, 68, 68];
    
    pdf.setFillColor(...statusColor);
    pdf.roundedRect(margin, currentY, 35, 8, 2, 2, 'F');
    pdf.setTextColor(255, 255, 255);
    setFont(pdf, 'small');
    pdf.text(defect.status.toUpperCase(), margin + 17.5, currentY + 5.5, { align: 'center' });
    pdf.setTextColor(0, 0, 0);
    
    // Risk level if critical
    if (defect.risk_level === 'Critical') {
      pdf.setFillColor(239, 68, 68);
      pdf.roundedRect(margin + 40, currentY, 30, 8, 2, 2, 'F');
      pdf.setTextColor(255, 255, 255);
      pdf.text('CRITICAL', margin + 55, currentY + 5.5, { align: 'center' });
      pdf.setTextColor(0, 0, 0);
    }
    
    currentY += 15;

    // Separator
    pdf.setLineWidth(0.5);
    pdf.line(margin, currentY, pageWidth - margin, currentY);
    currentY += PDF_CONFIG.sectionSpacing;

    // Defect Details
    currentY = addSectionHeading(pdf, currentY, 'Defect Details', 'heading');

    setFont(pdf, 'body');
    pdf.text(`Date Reported: ${formatDate(defect.date_reported)}`, margin, currentY);
    currentY += PDF_CONFIG.lineHeight;
    
    if (defect.location) {
      pdf.text(`Location: ${defect.location}`, margin, currentY);
      currentY += PDF_CONFIG.lineHeight;
    }
    
    if (defect.category) {
      pdf.text(`Category: ${defect.category}`, margin, currentY);
      currentY += PDF_CONFIG.lineHeight;
    }
    
    if (defect.risk_level) {
      pdf.text(`Risk Level: ${defect.risk_level}`, margin, currentY);
      currentY += PDF_CONFIG.lineHeight;
    }

    currentY += 3;

    // Description
    if (defect.description) {
      currentY = addSectionHeading(pdf, currentY, 'Description:', 'subheading');
      currentY = addWrappedText(pdf, currentY, defect.description, { 
        fontType: 'body' 
      });
      currentY += 3;
    }

    // Action Required
    if (defect.action_required) {
      currentY = addSectionHeading(pdf, currentY, 'Action Required:', 'subheading');
      currentY = addWrappedText(pdf, currentY, defect.action_required, { 
        fontType: 'body' 
      });
      currentY += 3;
    }

    // Resolution (if closed)
    if (defect.status === 'Closed' && defect.resolution) {
      currentY = addSectionHeading(pdf, currentY, 'Resolution:', 'subheading');
      currentY = addWrappedText(pdf, currentY, defect.resolution, { 
        fontType: 'body' 
      });
      
      if (defect.date_resolved) {
        setFont(pdf, 'body');
        pdf.text(`Date Resolved: ${formatDate(defect.date_resolved)}`, margin, currentY);
        currentY += PDF_CONFIG.lineHeight;
      }
    }

    // Add footer
    addReportFooter(pdf);

    // Generate filename
    const dateStr = formatDate(defect.date_reported).replace(/\//g, '-');
    const defectId = defect.reference_id || `DEF-${defect.id}`;
    const filename = savePDF(pdf, 'defect_report', defect.date_reported, `${defectId}_${dateStr}`);
    
    debug.log('Individual defect PDF exported', { 
      filename, 
      defectId: defect.id,
      status: defect.status
    });
    
    return true;
    
  } catch (error) {
    handlePDFError(error, 'individual defect export', 'Failed to generate defect report. Please try again.');
    return false;
  }
}